import React from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  useTheme,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  GlassModal,
  GlassModalContent,
  GlassTextField,
  ButtonTertiaryIcon,
  GlassColors,
  ButtonTertiary,
  NeumorphicButton,
} from './glassmorphism/GlassmorphismStyles';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { OrderViewModel } from '../configuration/api/apiSchema';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as RadioOff } from '../assets/icons/info/radio-off.svg';
import { ReactComponent as RadioOn } from '../assets/icons/info/radio-on.svg';

interface ProposalPriceModalProps {
  open: boolean;
  order: OrderViewModel;
  onClose: () => void;
  onSubmit: (
    orderId: string,
    proposedPrice?: number,
    coverLetter?: string,
  ) => void;
  loading: boolean;
}

const validationSchema = Yup.object({
  priceOption: Yup.string().required('Please select a pricing option'),
  customPrice: Yup.number().when('priceOption', {
    is: 'custom',
    then: (schema) =>
      schema
        .required('Custom price is required')
        .positive('Price must be greater than 0')
        .max(100000, 'Price cannot exceed £100,000'),
    otherwise: (schema) => schema.nullable(),
  }),
});

const ProposalPriceModal: React.FC<ProposalPriceModalProps> = ({
  open,
  order,
  onClose,
  onSubmit,
  loading,
}) => {
  const handleSubmit = (values: any) => {
    const proposedPrice =
      values.priceOption === 'listed' ? undefined : Number(values.customPrice);
    onSubmit(order.id!, proposedPrice, values.coverLetter);
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <GlassModal open={open} onClose={onClose} sx={{ zIndex: 999999 }}>
      <GlassModalContent
        sx={{
          maxWidth: { xs: '100%', tablet: 858 },
          width: { xs: '100%', tablet: '90%' },
          p: { xs: '16px 16px 32px', tablet: '24px 24px 32px 24px' },
          maxHeight: { xs: '100dvh', tablet: '95vh' },
          height: { xs: '100dvh', tablet: 'auto' },
          boxShadow: {
            xs: 'none',
            tablet: `-2.82px -2.82px 8.49px 0px #FFFFFF4D,
            14.15px 14.15px 22.64px -0.37px #0000001A,
            -17.54px -17.54px 28.66px 0px #FFFFFF0D,
            10px 10px 28.66px -11.7px #00000033,
            1.58px 1.58px 1.7px 0px #FFFFFF inset,
            -2.07px -2.64px 4.52px -1.51px #0000004D inset`,
          },
          borderRadius: { xs: 0, tablet: '32px' },
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={'16px'}
        >
          <Typography
            variant='h5'
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: '32px',
              color: isDark ? GlassColors.accent : GlassColors.secondary,
            }}
          >
            Submit Proposal
          </Typography>
          <ButtonTertiaryIcon
            onClick={onClose}
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: { xs: '16px', tablet: '24px' },
              right: { xs: '16px', tablet: '24px' },
              width: { xs: '32px', tablet: '40px' },
              height: { xs: '32px', tablet: '40px' },
              '& svg': {
                fontSize: { xs: '16px', tablet: '24px' },
              },
            }}
          >
            <CloseIcon />
          </ButtonTertiaryIcon>
        </Box>

        <Box mb={2}>
          <Typography
            variant='h6'
            sx={{
              fontFamily: '"Roboto", sans-serif',
              fontWeight: 700,
              letterSpacing: '.15px',
              fontSize: '16px',
              lineHeight: '175%',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              mb: 1,
            }}
          >
            {order.name}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              fontFamily: '"Roboto", sans-serif',
              fontWeight: 400,
              lineHeight: '150%',
              fontSize: '16px',
              letterSpacing: '.15px',
            }}
          >
            {order.description}
          </Typography>
          {order.price && (
            <Box display='flex' alignItems='center' gap={0.5} mt={2}>
              <Typography
                variant='body1'
                fontWeight='700'
                sx={{
                  color: isDark
                    ? GlassColors.dark.textPrimary
                    : GlassColors.light.textPrimary,
                  fontFamily: '"Roboto", sans-serif',
                  fontSize: '16px',
                  lineHeight: '175%',
                  letterSpacing: '.15px',
                  span: {
                    color: isDark ? GlassColors.accent : GlassColors.secondary,
                  },
                }}
              >
                Listed Price: <span>£{order.price}</span>
              </Typography>
            </Box>
          )}
        </Box>

        <Formik
          initialValues={{
            priceOption: 'listed',
            customPrice: '',
            coverLetter: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Box mb={values.priceOption === 'custom' ? 1 : 2}>
                <Typography
                  variant='body1'
                  sx={{
                    fontFamily: '"Motserrat", sans-serif',
                    fontWeight: 500,
                    color: isDark
                      ? GlassColors.dark.textPrimary
                      : GlassColors.light.textPrimary,
                    lineHeight: '166%',
                    letterSpacing: '.4px',
                    fontSize: '12px',
                    mb: 2,
                  }}
                >
                  Choose your pricing option:
                </Typography>

                <RadioGroup
                  value={values.priceOption}
                  onChange={(e) => setFieldValue('priceOption', e.target.value)}
                >
                  <FormControlLabel
                    value='listed'
                    control={
                      <Radio
                        icon={<RadioOff />}
                        checkedIcon={<RadioOn />}
                        sx={{
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                          p: '2px 4px 2px 0',
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: '"Roboto", sans-serif',
                          fontSize: '16px',
                          lineHeight: '175%',
                          letterSpacing: '.15px',
                          fontWeight: 700,
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                        }}
                      >
                        Apply with Listed Price (
                        {order.price ? `£${order.price}` : 'No price set'})
                      </Typography>
                    }
                    sx={{
                      ml: 0,
                      mb: '8px',
                    }}
                  />
                  <FormControlLabel
                    value='custom'
                    control={
                      <Radio
                        icon={<RadioOff />}
                        checkedIcon={<RadioOn />}
                        sx={{
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                          p: '2px 4px 2px 0',
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: '"Roboto", sans-serif',
                          fontSize: '16px',
                          lineHeight: '175%',
                          letterSpacing: '.15px',
                          fontWeight: 700,
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                        }}
                      >
                        Apply with Custom Price
                      </Typography>
                    }
                    sx={{
                      ml: 0,
                    }}
                  />
                </RadioGroup>
              </Box>
              {values.priceOption === 'custom' && (
                <Box mb={2}>
                  <Field name='customPrice'>
                    {({ field }: any) => (
                      <FormControl sx={{ width: 272 }}>
                        <FormLabel
                          sx={{
                            mb: 1,
                            fontSize: '14px',
                            fontWeight: 400,
                            letterSpacing: '.17px',
                            lineHeight: '143%',
                            color: isDark
                              ? GlassColors.dark.textPrimary
                              : GlassColors.light.textPrimary,
                          }}
                        >
                          Custom Price
                        </FormLabel>

                        <GlassTextField
                          {...field}
                          type='number'
                          error={touched.customPrice && !!errors.customPrice}
                          helperText={touched.customPrice && errors.customPrice}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontFamily: '"Roboto", sans-serif',
                            },
                          }}
                        />
                      </FormControl>
                    )}
                  </Field>
                </Box>
              )}
              <Box mb={2}>
                <Field name='coverLetter'>
                  {({ field }: any) => (
                    <FormControl fullWidth>
                      <FormLabel
                        sx={{
                          mb: 1,
                          fontSize: '12px',
                          lineHeight: '24px',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 500,
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                        }}
                      >
                        Cover letter
                      </FormLabel>

                      <GlassTextField
                        {...field}
                        placeholder='Describe your task in detail. For example: "I need to assemble an IKEA Malm dresser. I have all the parts, but no tools. The location is on the 2nd floor with no elevator.'
                        multiline
                        onChange={(e) =>
                          setFieldValue('coverLetter', e.target.value)
                        }
                        minRows={4}
                        error={
                          touched.coverLetter && Boolean(errors.coverLetter)
                        }
                        sx={{
                          '& .MuiInputBase-formControl': { p: '12px 24px' },
                          '& .MuiInputBase-input': {
                            fontFamily: '"Motserrat", sans-serif',
                          },
                          '&& textarea': {
                            height: {
                              xs: '156px !important',
                              sm: '84px !important',
                            },
                          },
                        }}
                      />
                    </FormControl>
                  )}
                </Field>
              </Box>

              <Box display='flex' gap={2} justifyContent='space-between'>
                <ButtonTertiary
                  variant='outlined'
                  onClick={onClose}
                  disabled={loading}
                  sx={{
                    '&&': {
                      width: { xs: 'auto', tablet: '68px' },
                      fontSize: { xs: '12px', tablet: '14px' },
                      height: { xs: '32px', tablet: '40px' },
                      px: { xs: '12px', tablet: '16px' },
                    },
                  }}
                >
                  Back
                </ButtonTertiary>
                <NeumorphicButton
                  type='submit'
                  disabled={loading}
                  sx={{
                    '&&': {
                      minWidth: 'unset',
                      height: { xs: '32px', tablet: '40px' },
                      fontSize: { xs: '12px', tablet: '14px' },
                      px: { xs: '12px', tablet: '16px' },
                    },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={20}
                        color='inherit'
                        sx={{ mr: 1 }}
                      />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </NeumorphicButton>
              </Box>
            </Form>
          )}
        </Formik>
      </GlassModalContent>
    </GlassModal>
  );
};

export default ProposalPriceModal;
